import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import dayjs from "dayjs"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterMemoriaCalculoValidacaoTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)

        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "DataReferencia": dayjs().format(DATE_FORMAT),
            "PUPARReferencia": 0,
            "PUMTMReferencia": 0,
            "SpreadCredito": 0,
            "PUPARCalculado": 0,
            "DeltaPUPAR": 0,
            "VariacaoPercentualPUPAR": 0,
            "PUMTMCalculado": 0,
            "DeltaPUMTM": 0,
            "VariacaoPercentualPUMTM": 0,
            "SpreadEmissao": 0,
            "PrazoMedioEmissao": 0,
            "PremioRecompra": 0,
            "PremioConversibilidade": 0,
            "PercentualPUPAR": 0,
            "DurationModificada": 0,
            "DurationMacaulay": 0
        }

        props.loading(true)

        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)

            console.error("Erro ao criar registro:")
            console.error("Mensagem:", err?.message)
            console.error("Response:", err?.response)
            console.error("Response data:", err?.response?.data)
            console.error("Status:", err?.response?.status)

            throw err
        })
    }

    const updateRow = async (newRow) => {
        const {
            ID,
            DataReferencia,
            PUPARReferencia,
            PUMTMReferencia,
            SpreadCredito,
            PUPARCalculado,
            DeltaPUPAR,
            VariacaoPercentualPUPAR,
            PUMTMCalculado,
            DeltaPUMTM,
            VariacaoPercentualPUMTM,
            SpreadEmissao,
            PrazoMedioEmissao,
            PremioRecompra,
            PremioConversibilidade,
            PercentualPUPAR,
            DurationModificada,
            DurationMacaulay
        } = newRow

        const body = {
            "DataReferencia": DataReferencia,
            "PUPARReferencia": PUPARReferencia,
            "PUMTMReferencia": PUMTMReferencia,
            "SpreadCredito": SpreadCredito,
            "PUPARCalculado": PUPARCalculado,
            "DeltaPUPAR": DeltaPUPAR,
            "VariacaoPercentualPUPAR": VariacaoPercentualPUPAR,
            "PUMTMCalculado": PUMTMCalculado,
            "DeltaPUMTM": DeltaPUMTM,
            "VariacaoPercentualPUMTM": VariacaoPercentualPUMTM,
            "SpreadEmissao": SpreadEmissao,
            "PrazoMedioEmissao": PrazoMedioEmissao,
            "PremioRecompra": PremioRecompra,
            "PremioConversibilidade": PremioConversibilidade,
            "PercentualPUPAR": PercentualPUPAR,
            "DurationModificada": DurationModificada,
            "DurationMacaulay": DurationMacaulay
        }

        props.loading(true)

        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList
            .map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`)
            .join('&')

        props.loading(true)

        try {
            const response = await datalakeService.loadPaginationEndpoint(
                props.endpoint,
                query,
                limit,
                offset
            )

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            props.callbackError(error)
        } finally {
            props.loading(false)
        }
    }

    const changePage = async (endpoint) => {
        props.loading(true)

        try {
            const response = await datalakeService.paginate(endpoint)

            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count, next, previous })
            setData(list)
        } catch (error) {
            props.callbackError?.(error)
        } finally {
            props.loading(false)
        }
    }

    const dateColumn = (field, minWidth = 150) => ({
        field,
        headerName: field,
        flex: 1,
        minWidth,
        typeComponent: 'date',
        editable: true,
        renderCell: (params) =>
            params.value ? dayjs(params.value).format(DATE_FORMAT_SHOW) : ''
    })

    const numberColumn = (field, minWidth = 180) => ({
        field,
        headerName: field,
        flex: 1,
        minWidth,
        typeComponent: 'number',
        type: 'number',
        editable: true
    })

    const columns = [
        { field: 'ID', headerName: '#', disableColumnMenu: true, disableFilter: true, flex: true, minWidth: 80 },

        dateColumn('DataReferencia'),

        numberColumn('PUPARReferencia', 200),
        numberColumn('PUMTMReferencia', 200),
        numberColumn('SpreadCredito'),
        numberColumn('PUPARCalculado', 200),
        numberColumn('DeltaPUPAR'),
        numberColumn('VariacaoPercentualPUPAR', 220),
        numberColumn('PUMTMCalculado', 200),
        numberColumn('DeltaPUMTM'),
        numberColumn('VariacaoPercentualPUMTM', 220),
        numberColumn('SpreadEmissao'),
        numberColumn('PrazoMedioEmissao', 200),
        numberColumn('PremioRecompra'),
        numberColumn('PremioConversibilidade', 220),
        numberColumn('PercentualPUPAR'),
        numberColumn('DurationModificada', 200),
        numberColumn('DurationMacaulay', 200),

        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true}
                useDeleteButton={false}
            />
        </div>
    )
}

ParameterMemoriaCalculoValidacaoTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterMemoriaCalculoValidacaoTable